import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import AccessGateModal from '../components/AccessGateModal';
import useAccessGate from '../hooks/useAccessGate';
import { Save, FileText, Download } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import CVPreview from '../components/CVPreview';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 800;
  color: #1f2937;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #ffffff;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.03);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  outline: none;
  transition: border-color 0.2s;
  &:focus {
    border-color: #6366f1;
  }
`;

const TextArea = styled.textarea`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  outline: none;
  min-height: 120px;
  resize: vertical;
  transition: border-color 0.2s;
  &:focus {
    border-color: #6366f1;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 700;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  color: #374151;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 0.75rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ButtonBar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props =>
    props.variant === 'secondary'
      ? 'white'
      : 'linear-gradient(135deg, #6366f1, #8b5cf6)'};
  color: ${props => (props.variant === 'secondary' ? '#374151' : 'white')};
  border: ${props =>
    props.variant === 'secondary' ? '1px solid #e5e7eb' : 'none'};
  padding: 0.75rem 1rem;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
`;

const PreviewCard = styled.div`
  margin-top: 2rem;
  background: white;
  border-radius: 16px;
  overflow: auto;
`;

const parseAwards = (raw) => {
  return raw
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const parseSkillGroups = (raw) => {
  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split(':');
      if (parts.length < 2) {
        return { title: line, items: [] };
      }
      const title = parts.shift().trim();
      const itemsRaw = parts.join(':');
      const items = itemsRaw
        .split(/;|,/)
        .map((item) => item.trim())
        .filter(Boolean);
      return { title, items };
    })
    .filter((group) => group.title);
};

const CVGenerator = () => {
  const { gate, requirePremium, closeGate } = useAccessGate();
  const [loading, setLoading] = useState(false);
  const previewRef = useRef(null);

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    social: '',
    summary: '',
    awards: '',
    skills: ''
  });

  const [experiences, setExperiences] = useState([
    { role: '', company: '', startDate: '', endDate: '', description: '' }
  ]);

  const [education, setEducation] = useState([
    { school: '', degree: '', startDate: '', endDate: '' }
  ]);

  const [previewData, setPreviewData] = useState(null);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleExperienceChange = (index, field, value) => {
    setExperiences(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleEducationChange = (index, field, value) => {
    setEducation(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addExperience = () => {
    setExperiences(prev => [
      ...prev,
      { role: '', company: '', startDate: '', endDate: '', description: '' }
    ]);
  };

  const addEducation = () => {
    setEducation(prev => [
      ...prev,
      { school: '', degree: '', startDate: '', endDate: '' }
    ]);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const allowed = await requirePremium();
    if (!allowed) return;
    if (!form.fullName || !form.email) {
      toast.error('Ad soyad ve e-posta zorunludur.');
      return;
    }

    setLoading(true);

    const experienceArr = experiences
      .filter(exp => exp.role || exp.company || exp.description)
      .map(exp => {
        const tasks = exp.description
          .split(/\r?\n|\u2022|-/)
          .map(t => t.trim())
          .filter(Boolean);

        return {
          role: exp.role,
          company: exp.company,
          date: [exp.startDate, exp.endDate].filter(Boolean).join(' - '),
          tasks
        };
      });

    const educationArr = education
      .filter(ed => ed.school || ed.degree)
      .map(ed => ({
        school: ed.school,
        degree: ed.degree,
        date: [ed.startDate, ed.endDate].filter(Boolean).join(' - ')
      }));

    const preview = {
      name: form.fullName,
      email: form.email,
      phone: form.phone,
      address: form.address,
      website: form.website,
      social: form.social,
      summary: form.summary,
      experience: experienceArr,
      education: educationArr,
      awards: parseAwards(form.awards),
      skills: parseSkillGroups(form.skills)
    };

    setPreviewData(preview);
    setLoading(false);
    toast.success('ATS CV Önizlemesi oluşturuldu');
  };

  const handleDownload = async () => {
    const allowed = await requirePremium();
    if (!allowed) return;
    if (!previewData) {
      toast.error('Önce CV Önizlemesini oluşturun.');
      return;
    }

    if (!previewRef.current) {
      toast.error('Önizleme alanı bulunamadı.');
      return;
    }

    const element = previewRef.current;

    const opt = {
      margin: 0,
      filename: 'ats-uyumlu-cv.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    html2pdf()
      .from(element)
      .set(opt)
      .save()
      .catch(() => {
        toast.error('PDF oluşturulurken bir hata oluştu.');
      });
  };

  return (
    <>
      <Container>
        <Title>ATS Uyumlu CV Olustur</Title>
        <Subtitle>
          Bu form, siyah-beyaz sade ATS formatına gore hazırlanmıştır. Başlıkları doldurun,
          önizlemeyi kontrol edin ve PDF olarak indirin.
        </Subtitle>

        <Form onSubmit={onSubmit}>
          <SectionTitle>Temel Bilgiler</SectionTitle>
          <Grid>
            <Field>
              <Label>Ad Soyad*</Label>
              <Input
                name="fullName"
                value={form.fullName}
                onChange={handleChange}
                placeholder="Orn: Sedat Ates"
              />
            </Field>
            <Field>
              <Label>E-posta*</Label>
              <Input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="ornek@mail.com"
              />
            </Field>
            <Field>
              <Label>Telefon</Label>
              <Input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="05xx xxx xx xx"
              />
            </Field>
            <Field>
              <Label>Adres</Label>
              <Input
                name="address"
                value={form.address}
                onChange={handleChange}
                placeholder="Orn: Ataturk Mah. Gazi Blv. No: 12"
              />
            </Field>
            <Field>
              <Label>Website</Label>
              <Input
                name="website"
                value={form.website}
                onChange={handleChange}
                placeholder="www.ornek.com"
              />
            </Field>
            <Field>
              <Label>Sosyal</Label>
              <Input
                name="social"
                value={form.social}
                onChange={handleChange}
                placeholder="@kullaniciadi"
              />
            </Field>
          </Grid>

          <SectionTitle>Profesyonel Özet</SectionTitle>
          <Field>
            <TextArea
              name="summary"
              value={form.summary}
              onChange={handleChange}
              placeholder="Kisa profesyonel ozet"
            />
          </Field>

          <SectionTitle>Eğitim</SectionTitle>
          {education.map((ed, idx) => (
            <Row key={idx}>
              <Input
                placeholder="Okul"
                value={ed.school}
                onChange={(e) =>
                  handleEducationChange(idx, 'school', e.target.value)
                }
              />
              <Input
                placeholder="Derece"
                value={ed.degree}
                onChange={(e) =>
                  handleEducationChange(idx, 'degree', e.target.value)
                }
              />
              <Input
                placeholder="Başlangıç"
                value={ed.startDate}
                onChange={(e) =>
                  handleEducationChange(idx, 'startDate', e.target.value)
                }
              />
              <Input
                placeholder="Bitiş"
                value={ed.endDate}
                onChange={(e) =>
                  handleEducationChange(idx, 'endDate', e.target.value)
                }
              />
            </Row>
          ))}
          <Button
            type="button"
            variant="secondary"
            onClick={addEducation}
            style={{ marginTop: '0.5rem' }}
          >
            <FileText size={16} /> Egitim Ekle
          </Button>

          <SectionTitle>Is Deneyimi</SectionTitle>
          {experiences.map((exp, idx) => (
            <Row key={idx}>
              <Input
                placeholder="Rol"
                value={exp.role}
                onChange={(e) =>
                  handleExperienceChange(idx, 'role', e.target.value)
                }
              />
              <Input
                placeholder="Kurum"
                value={exp.company}
                onChange={(e) =>
                  handleExperienceChange(idx, 'company', e.target.value)
                }
              />
              <Input
                placeholder="Başlangıç"
                value={exp.startDate}
                onChange={(e) =>
                  handleExperienceChange(idx, 'startDate', e.target.value)
                }
              />
              <Input
                placeholder="Bitiş"
                value={exp.endDate}
                onChange={(e) =>
                  handleExperienceChange(idx, 'endDate', e.target.value)
                }
              />
              <TextArea
                placeholder="Açıklama (madde madde yazabilirsiniz)"
                value={exp.description}
                onChange={(e) =>
                  handleExperienceChange(idx, 'description', e.target.value)
                }
              />
            </Row>
          ))}
          <Button
            type="button"
            variant="secondary"
            onClick={addExperience}
            style={{ marginTop: '0.5rem' }}
          >
            <FileText size={16} /> Deneyim Ekle
          </Button>

          <SectionTitle>Oduller</SectionTitle>
          <Field>
            <TextArea
              name="awards"
              value={form.awards}
              onChange={handleChange}
              placeholder="Her ödülü ayrı satıra yazın"
            />
          </Field>

          <SectionTitle>Yetenekler</SectionTitle>
          <Field>
            <TextArea
              name="skills"
              value={form.skills}
              onChange={handleChange}
              placeholder="Başlık: madde1; madde2\nBaslık: madde1; madde2"
            />
          </Field>

          <ButtonBar>
            <Button type="submit" disabled={loading}>
              <Save size={16} />
              {loading ? 'Oluşturuluyor...' : 'ATS CV Önizle'}
            </Button>
            {previewData && (
              <Button type="button" variant="secondary" onClick={handleDownload}>
                <Download size={16} />
                PDF Olarak İndir
              </Button>
            )}
          </ButtonBar>
        </Form>

        {previewData && (
          <PreviewCard ref={previewRef}>
            <CVPreview data={previewData} />
          </PreviewCard>
        )}
      </Container>
      <AccessGateModal type={gate} onClose={closeGate} />
    </>
  );
};
export default CVGenerator;





